import { Amplify } from 'aws-amplify';
import {
  signOut,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import cookies from 'js-cookie';
import { AUTH_TOKEN } from '../constants-togather/auth';
import {
  COGNITO_IDENTITY_FIELDS,
  SUPPLIER_GROUP_TYPE,
} from '../constants-togather/cognito';
import config from '../../app-config';

import paths from '../constants/paths';
import { captureError } from './monitoring';
import { trackIdentity } from './analytics/analytics';

export const cognitoSignOut = async (): Promise<void> => signOut();

Amplify.configure(
  {
    Auth: {
      Cognito: {
        userPoolClientId: config.cognitoClientId,
        userPoolId: config.cognitoPoolId,
        loginWith: {
          oauth: {
            domain: config.cognitoPoolDomain,
            redirectSignIn: [paths.LOGIN],
            redirectSignOut: [paths.LOGOUT],
            scopes: ['email', 'profile', 'openid', 'phone'],
            responseType: 'code',
            providers: ['Google'],
          },
          email: true,
        },
      },
    },
  },
  {
    ssr: true,
  }
);

// Export a function to handle Google sign in with state
export const signInWithGoogle = async (redirectPath?: string) => {
  try {
    await signInWithRedirect({
      provider: 'Google',
      customState: redirectPath || window.location.pathname,
    });
  } catch (error) {
    captureError(error, {
      tags: {
        source: 'google-signin',
      },
    });
    throw error;
  }
};

Hub.listen('auth', async ({ payload }) => {
  switch (payload.event) {
    case 'signedIn':
      try {
        const session = await fetchAuthSession();
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');

        const accessToken = session.tokens?.accessToken?.toString();
        const identityDetails = session.tokens?.idToken?.payload;

        if (!accessToken || !identityDetails) {
          throw new Error('Invalid session tokens');
        }

        const userId = identityDetails[
          COGNITO_IDENTITY_FIELDS.DATABASE_ID
        ] as string;
        const groups = identityDetails[
          COGNITO_IDENTITY_FIELDS.GROUPS
        ] as string[];
        const isSupplierUser = groups.includes(SUPPLIER_GROUP_TYPE);

        cookies.set(AUTH_TOKEN, accessToken, {
          ...(process.env.REACT_APP_COOKIE_SECURE && {
            secure: process.env.REACT_APP_COOKIE_SECURE === 'true',
          }),
          expires: 21,
        });

        trackIdentity(userId, {
          email: identityDetails[COGNITO_IDENTITY_FIELDS.EMAIL],
          lastLogin: new Date().toISOString(),
        });

        // Use state parameter for redirect if available
        const redirectPath =
          state ||
          (isSupplierUser ? paths.SUPPLIER_DASHBOARD : paths.DASHBOARD);
        window.location.href = redirectPath;
      } catch (error) {
        await signOut();
        captureError(error, {
          tags: {
            source: 'auth',
          },
        });
      }
      break;

    case 'signInWithRedirect_failure':
      captureError(new Error('OAuth redirect failed'), {
        tags: { source: 'oauth-redirect' },
        extra: payload,
      });
      break;

    default:
      break;
  }
});
